import type { CheckSendCountryItem, CheckSendLogisticsItem } from './types'
import { goodsPostCalList, queryCountryList } from '@user-center/api/common'
import { getTransfersTextByKey } from '@user-center/utils/transferText'
import { ElMessage } from 'element-plus'
import { computed, ref } from 'vue'
import { useMallShopId } from '@/composables/useMallShopId'

interface CheckSendAvailabilityParams {
  countryCode: string
  goodCode?: string | number | null
  goodType?: string | null
  weight?: string | number | null
  showError?: boolean
}

interface CheckSendAvailabilityResult {
  isAllowed: boolean
  list: CheckSendLogisticsItem[]
  matchedBy: Array<'goodCode' | 'goodType'>
}

const DEFAULT_WEIGHT = '1'
const countryListCache = new Map<string, CheckSendCountryItem[]>()
const availabilityCache = new Map<string, CheckSendAvailabilityResult>()

function normalizeGoodType(raw?: string | null) {
  return String(raw || '').trim()
}

function buildAvailabilityCacheKey(shopId: string, params: CheckSendAvailabilityParams) {
  return [
    shopId,
    String(params.countryCode || '').trim(),
    String(params.goodCode || '').trim(),
    normalizeGoodType(params.goodType),
    String(params.weight || DEFAULT_WEIGHT).trim(),
  ].join('::')
}

function dedupeLogistics(list: any[]) {
  return list.filter((entry: any, index: number, self: any[]) => {
    return index === self.findIndex(currentItem => String(currentItem.id || '') === String(entry.id || ''))
  })
}

function isAllowedLogistics(item: any) {
  return item?.isAllow === undefined || item?.isAllow === null || Number(item?.isAllow) === 1
}

export function useCheckSendAvailability() {
  const { getShopId, shopId } = useMallShopId()
  const loadingCountries = ref(false)
  const querying = ref(false)
  const countries = ref<CheckSendCountryItem[]>([])
  const transfersMap = ref<Record<string, string>>({})

  const countryOptions = computed(() => countries.value.map((item) => {
    const label = String(item.aliasName || item.country || '').trim()
    return {
      ...item,
      label: label ? getTransfersTextByKey(label) : '',
    }
  }))

  function mergeTransfersMap(nextMap?: Record<string, string>) {
    if (!nextMap || typeof nextMap !== 'object') {
      return
    }

    transfersMap.value = {
      ...transfersMap.value,
      ...nextMap,
    }
  }

  async function loadCountryList(options?: { force?: boolean, showError?: boolean }) {
    const currentShopId = getShopId()
    if (!currentShopId) {
      if (options?.showError !== false) {
        ElMessage.warning('未获取到店铺信息，请刷新页面后重试')
      }
      return []
    }

    if (!options?.force && countryListCache.has(currentShopId)) {
      const cached = countryListCache.get(currentShopId) || []
      countries.value = cached
      return cached
    }

    loadingCountries.value = true
    try {
      const res: any = await queryCountryList({ shopId: currentShopId })
      mergeTransfersMap(res?.transfersMap)
      const list = Array.isArray(res?.data) ? res.data : []
      countryListCache.set(currentShopId, list)
      countries.value = list
      return list
    }
    catch (error: any) {
      if (options?.showError !== false) {
        ElMessage.error(error?.msg || error?.message || '目的地加载失败')
      }
      return []
    }
    finally {
      loadingCountries.value = false
    }
  }

  async function queryAvailability(params: CheckSendAvailabilityParams): Promise<CheckSendAvailabilityResult> {
    const currentShopId = getShopId()
    if (!currentShopId) {
      const message = '未获取到店铺信息，请刷新页面后重试'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    const countryCode = String(params.countryCode || '').trim()
    const goodCode = String(params.goodCode || '').trim()
    const goodType = normalizeGoodType(params.goodType)
    const weight = String(params.weight || DEFAULT_WEIGHT).trim()

    if (!countryCode) {
      const message = '请选择目的地'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    if (!goodCode && !goodType) {
      const message = '请至少传入商品编码或商品分类'
      if (params.showError !== false) {
        ElMessage.warning(message)
      }
      throw new Error(message)
    }

    const cacheKey = buildAvailabilityCacheKey(currentShopId, {
      countryCode,
      goodCode,
      goodType,
      weight,
    })
    if (availabilityCache.has(cacheKey)) {
      return availabilityCache.get(cacheKey)!
    }

    querying.value = true
    try {
      const res: any = await goodsPostCalList({
        shopId: currentShopId,
        cargoTypeCode: '',
        countryCode,
        warehouseCode: '',
        weight,
        // These are the categoryName/categoryId returned by traffic-item-get.
        goodType,
        goodCode,
      })

      mergeTransfersMap(res?.transfersMap)
      const list = dedupeLogistics(Array.isArray(res?.data) ? res.data : [])
      const result: CheckSendAvailabilityResult = {
        isAllowed: list.some(isAllowedLogistics),
        list,
        matchedBy: [
          ...(goodCode ? ['goodCode' as const] : []),
          ...(goodType ? ['goodType' as const] : []),
        ],
      }

      availabilityCache.set(cacheKey, result)
      return result
    }
    catch (error: any) {
      if (params.showError !== false) {
        ElMessage.error(error?.msg || error?.message || '可寄结果查询失败')
      }
      throw error
    }
    finally {
      querying.value = false
    }
  }

  return {
    countries,
    countryOptions,
    loadingCountries,
    querying,
    shopId,
    transfersMap,
    mergeTransfersMap,
    loadCountryList,
    queryAvailability,
  }
}

export type {
  CheckSendAvailabilityParams,
  CheckSendAvailabilityResult,
}
